import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Nav, Spinner, Alert, Modal, Button } from 'react-bootstrap';
import { getStorage, ref, listAll, getDownloadURL } from 'firebase/storage';
import { motion } from 'framer-motion';
import './Gallery.css'; // Create this CSS file for custom styles

const Gallery = () => {
  const [images, setImages] = useState({});
  const [activeCategory, setActiveCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [rotation, setRotation] = useState(0);
  const modalRef = useRef(null);
  const storage = getStorage();

  const categories = [
    'All', 'Certificate', 'Training', 'Achievements',
    'Events', 'Workshops', 'Cultural'
  ];

  // Enhanced clean caption function
  const cleanCaption = (filename) => {
    // Remove extension and numbers
    let name = filename.replace(/\.[^/.]+$/, "")
                       .replace(/[0-9]/g, '')
                       .replace(/_/g, ' ')
                       .trim();
    
    // Capitalize and clean up
    return name.split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')
              .replace(/\s+/g, ' ');
  };

  // Block screenshots and protections
  useEffect(() => {
    const blockScreenshot = () => {
      // Block desktop screenshots
      document.addEventListener('keydown', (e) => {
        if (e.key === 'PrintScreen' || (e.ctrlKey && e.key === 'p')) {
          e.preventDefault();
          alert('Screenshots are disabled in the gallery');
        }
      });

      // Block mobile screenshot detection (works on most devices)
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
      document.body.style.touchCallout = 'none';
    };

    fetchImages();
    blockScreenshot();

    // Cleanup
    return () => {
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.touchCallout = '';
    };
  }, []);

  // Add screenshot and right-click protection
  useEffect(() => {
    const preventActions = (e) => {
      e.preventDefault();
      e.stopPropagation();
      alert('Screenshots and downloads are not allowed for security reasons.');
    };

    // Prevent right click
    document.addEventListener('contextmenu', preventActions);
    // Prevent keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      if (
        // Windows: Ctrl + Shift + S, Ctrl + S, Ctrl + P
        (e.ctrlKey && (e.key === 's' || e.key === 'p')) ||
        // Mac: Cmd + S, Cmd + P
        (e.metaKey && (e.key === 's' || e.key === 'p')) ||
        // PrintScreen key
        e.key === 'PrintScreen'
      ) {
        preventActions(e);
      }
    });
    
    return () => {
      document.removeEventListener('contextmenu', preventActions);
      document.removeEventListener('keydown', preventActions);
    };
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const imagesData = {};

      await Promise.all(categories.map(async (category) => {
        if (category === 'All') return;
        
        const folderRef = ref(storage, `gallery/${category.toLowerCase()}`);
        const result = await listAll(folderRef);

        imagesData[category] = await Promise.all(
          result.items.map(async (itemRef) => ({
            url: await getDownloadURL(itemRef),
            name: itemRef.name,
            category
          }))
        );
      }));

      setImages(imagesData);
    } catch (err) {
      setError('Failed to load gallery. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredImages = () => {
    return activeCategory === 'All' 
      ? Object.values(images).flat() 
      : images[activeCategory] || [];
  };

  const openImageModal = (image) => {
    setSelectedImage(image);
    setRotation(0);
  };

  const rotateImage = () => setRotation(prev => (prev + 90) % 360);

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <Container fluid className="gallery-container py-4">
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <h2 className="text-center mb-4 gallery-title">
          <span>Our</span> Precious Moments
        </h2>
        
        <p className="text-center mb-4 gallery-subtitle">
          Explore memories from various GSK College events and activities
        </p>
      </motion.div>

      <Nav variant="pills" className="justify-content-center mb-4 category-nav">
        {categories.map((category) => (
          <Nav.Item key={category}>
            <Nav.Link
              eventKey={category}
              active={activeCategory === category}
              onClick={() => setActiveCategory(category)}
              className="category-link"
            >
              {category}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      {loading ? (
        <div className="text-center my-5 py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading beautiful memories...</p>
        </div>
      ) : error ? (
        <Alert variant="danger" className="text-center">
          {error}
        </Alert>
      ) : (
        <Row className="g-4">
          {getFilteredImages().length === 0 ? (
            <Col className="text-center py-5">
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                <h5 className="text-muted">No images found in this category</h5>
              </motion.div>
            </Col>
          ) : (
            getFilteredImages().map((image, index) => (
              <Col key={index} xs={12} sm={6} md={4} lg={3}>
                <motion.div
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <Card className="gallery-card h-100" style={{ 
                    overflow: 'hidden',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}>
                    <div className="image-wrapper" 
                      onClick={() => openImageModal(image)}
                      style={{ 
                        cursor: 'pointer',
                        position: 'relative',
                        aspectRatio: '1',
                        overflow: 'hidden'
                      }}
                    >
                      <img
                        src={image.url}
                        alt={cleanCaption(image.name)}
                        className="gallery-image"
                        loading="lazy"
                        style={{ 
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.3s ease'
                        }}
                      />
                      <div className="image-overlay"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'rgba(0,0,0,0.9)', // Changed to darker black overlay
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0,
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <span style={{ 
                          color: '#fff',
                          fontSize: '1.2rem',
                          fontWeight: '600',
                          letterSpacing: '0.5px'
                        }}>
                          Click to View
                        </span>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              </Col>
            ))
          )}
        </Row>
      )}

      {/* Modal with details */}
      <Modal 
        show={!!selectedImage} 
        onHide={() => setSelectedImage(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedImage && cleanCaption(selectedImage.name)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedImage && (
            <motion.div
              animate={{ rotate: rotation }}
              transition={{ type: 'spring', stiffness: 100 }}
            >
              <img
                src={selectedImage.url}
                alt={cleanCaption(selectedImage.name)}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
            </motion.div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="outline-primary" 
            onClick={rotateImage}
          >
            Rotate
          </Button>
          <Button 
            variant="secondary" 
            onClick={() => setSelectedImage(null)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .image-wrapper:hover .image-overlay {
          opacity: 1;
        }
        .image-wrapper:hover img {
          transform: scale(1.05);
        }
      `}</style>
    </Container>
  );
};

export default Gallery;